import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Briefcase, CheckCircle2, Flag, Star, Activity, ShieldAlert, Ban, Play, ArrowLeft,
} from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Loading, EmptyState } from "@/components/EmptyState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    const [s, u, j, r] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/jobs"), api.get("/admin/reports"),
    ]);
    setStats(s.data); setUsers(u.data); setJobs(j.data); setReports(r.data); setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const setUserStatus = async (id, status) => {
    try { await api.post(`/admin/users/${id}/status`, { status }); toast.success("Status diperbarui"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelJob = async (id) => {
    try { await api.post(`/admin/jobs/${id}/cancel`); toast.success("Pekerjaan dibatalkan"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setReportStatus = async (id, status) => {
    try { await api.post(`/admin/reports/${id}/status`, { status }); toast.success("Laporan diperbarui"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-page">
      <button onClick={() => navigate("/app")} className="flex items-center gap-1 text-slate-500 mb-3 hover:text-slate-800"><ArrowLeft size={18} /> Ke aplikasi</button>
      <h1 className="text-2xl sm:text-3xl font-extrabold mb-1 flex items-center gap-2"><ShieldAlert className="text-blue-600" /> Admin Dashboard</h1>
      <p className="text-slate-500 mb-6">Kelola pengguna, pekerjaan, dan laporan.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <SCard icon={Users} label="Pengguna" value={stats.total_users} />
        <SCard icon={Briefcase} label="Total Pekerjaan" value={stats.total_jobs} />
        <SCard icon={Activity} label="Aktif" value={stats.open_jobs + stats.assigned_jobs + stats.in_progress_jobs} />
        <SCard icon={CheckCircle2} label="Selesai" value={stats.completed_jobs} />
        <SCard icon={Briefcase} label="Lamaran" value={stats.total_applications} />
        <SCard icon={Star} label="Ulasan" value={stats.total_reviews} />
        <SCard icon={Flag} label="Laporan Baru" value={stats.open_reports} />
        <SCard icon={Ban} label="Dibatalkan" value={stats.cancelled_jobs} />
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-5">
          <TabsTrigger value="users" data-testid="admin-tab-users">Pengguna</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="admin-tab-jobs">Pekerjaan</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Laporan</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{u.name} {u.role === "admin" && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-1">Admin</span>}</p>
                  <p className="text-sm text-slate-500 truncate">{u.email} · {u.city || "-"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{u.status === "active" ? "Aktif" : "Ditangguhkan"}</span>
                  {u.role !== "admin" && (u.status === "active" ? (
                    <Button data-testid={`suspend-${u.id}`} size="sm" variant="outline" onClick={() => setUserStatus(u.id, "suspended")} className="text-rose-600 border-rose-200 rounded-lg"><Ban size={14} className="mr-1" /> Tangguhkan</Button>
                  ) : (
                    <Button data-testid={`activate-${u.id}`} size="sm" onClick={() => setUserStatus(u.id, "active")} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg"><Play size={14} className="mr-1" /> Aktifkan</Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="jobs">
          <div className="space-y-2">
            {jobs.map((j) => (
              <div key={j.id} data-testid={`admin-job-${j.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/pekerjaan/${j.id}`)}>
                  <p className="font-semibold text-slate-900 truncate">{j.title}</p>
                  <p className="text-sm text-slate-500 truncate">{j.employer?.name} · {j.city}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={j.status} />
                  {!["COMPLETED", "CANCELLED"].includes(j.status) && (
                    <Button data-testid={`admin-cancel-${j.id}`} size="sm" variant="outline" onClick={() => cancelJob(j.id)} className="text-rose-600 border-rose-200 rounded-lg">Batalkan</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports">
          {reports.length === 0 ? (
            <EmptyState icon={Flag} title="Belum ada laporan" />
          ) : (
            <div className="space-y-2">
              {reports.map((r) => (
                <div key={r.id} data-testid={`admin-report-${r.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{r.reason}</p>
                      <p className="text-sm text-slate-500">Oleh: {r.reporter?.name} · {formatDate(r.created_at)}</p>
                      {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
                      <div className="flex gap-3 mt-2 text-xs">
                        {r.target_user && <button className="text-blue-600 font-semibold" onClick={() => navigate(`/pengguna/${r.target_user.id}`)}>Lihat pengguna: {r.target_user.name}</button>}
                        {r.target_job && <button className="text-blue-600 font-semibold" onClick={() => navigate(`/pekerjaan/${r.target_job.id}`)}>Lihat pekerjaan: {r.target_job.title}</button>}
                      </div>
                    </div>
                    <StatusBadge status={r.status} kind="report" />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Select value={r.status} onValueChange={(v) => setReportStatus(r.id, v)}>
                      <SelectTrigger data-testid={`report-status-${r.id}`} className="w-44 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPEN">Baru</SelectItem>
                        <SelectItem value="REVIEWING">Ditinjau</SelectItem>
                        <SelectItem value="RESOLVED">Selesai</SelectItem>
                        <SelectItem value="DISMISSED">Diabaikan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const SCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4">
    <Icon size={18} className="text-blue-600 mb-2" />
    <p className="text-2xl font-extrabold text-slate-900">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);
